import React, { useState } from "react";
import { Button } from "./ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "./ui/dialog";
import { Textarea } from "./ui/textarea";
import { HelpCircle, Loader2, Send, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * Section-level "Need help?" button + modal.
 *
 * When clicked, opens a Sentry-branded dialog where the client can type a
 * question about the current section. Posts to /api/client/onboarding/help-request
 * which immediately emails admins and records the request on the onboarding.
 */
export default function HelpRequestButton({ token, sectionId, sectionTitle, contactEmail }) {
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState("");
  const [sending, setSending] = useState(false);
  const [done, setDone] = useState(false);

  const submit = async () => {
    const trimmed = q.trim();
    if (!trimmed) { toast.error("Please type a question first"); return; }
    setSending(true);
    try {
      await axios.post(`${API}/client/onboarding/help-request`, {
        token, section_id: sectionId, question: trimmed, contact_email: contactEmail || undefined,
      });
      setDone(true);
      toast.success("Sent! We'll get back to you shortly.");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to send — please try again");
    } finally {
      setSending(false);
    }
  };

  const reset = () => {
    setQ("");
    setDone(false);
  };

  return (
    <>
      <button
        type="button"
        onClick={() => { reset(); setOpen(true); }}
        className="inline-flex items-center gap-1.5 text-xs font-medium text-brand-primary/80 hover:text-brand-primary hover:bg-brand-primary/5 px-2.5 py-1.5 rounded-md transition border border-transparent hover:border-brand-primary/20"
        data-testid={`help-request-trigger-${sectionId}`}
      >
        <HelpCircle size={14} />
        Need help with this section?
      </button>

      <Dialog open={open} onOpenChange={(v) => { setOpen(v); if (!v) reset(); }}>
        <DialogContent className="bg-white text-slate-900 max-w-md">
          <DialogHeader>
            <DialogTitle className="text-brand-primary flex items-center gap-2">
              <HelpCircle size={18} />
              {done ? "We got it!" : "Ask the Sentry team"}
            </DialogTitle>
            <DialogDescription>
              {done ? (
                <>Thanks for reaching out — our onboarding team will reply by email within a few business hours.</>
              ) : (
                <>Stuck on <span className="font-semibold text-slate-900">{sectionTitle}</span>? Type your question below and our team will reply by email.</>
              )}
            </DialogDescription>
          </DialogHeader>

          {!done ? (
            <div className="space-y-3 mt-2">
              <Textarea
                value={q}
                onChange={(e) => setQ(e.target.value)}
                placeholder="e.g., I don't know where to find our firewall model. Where should I look?"
                rows={5}
                maxLength={2000}
                className="resize-none"
                data-testid="help-request-textarea"
              />
              <div className="flex items-center justify-between text-xs text-slate-500">
                <span>{q.length}/2000</span>
                <span>Replies usually within 4 business hours.</span>
              </div>
            </div>
          ) : (
            <div className="mt-2 flex items-start gap-3 bg-emerald-50 border border-emerald-200 rounded-md px-4 py-3">
              <CheckCircle2 size={18} className="text-emerald-600 shrink-0 mt-0.5" />
              <div className="text-sm text-emerald-900">
                <div className="font-semibold">Question sent</div>
                <div className="text-xs text-emerald-800/80 mt-0.5">We&apos;ll reply to <span className="font-medium">{contactEmail}</span>. You can keep filling out the rest of the wizard in the meantime.</div>
              </div>
            </div>
          )}

          <DialogFooter>
            {!done ? (
              <>
                <Button variant="outline" onClick={() => setOpen(false)} className="border-slate-300 text-slate-700 hover:bg-slate-100" data-testid="help-request-cancel">
                  Cancel
                </Button>
                <Button onClick={submit} disabled={sending || !q.trim()} className="font-semibold" style={{ background: "#00C7FF", color: "#0F1644" }} data-testid="help-request-send">
                  {sending ? <Loader2 className="animate-spin" size={14}/> : <><Send size={14} className="mr-1.5"/>Send to team</>}
                </Button>
              </>
            ) : (
              <Button onClick={() => setOpen(false)} className="font-semibold" style={{ background: "#0F1644", color: "#fff" }} data-testid="help-request-close">
                Close
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
